/**
 * Admin Layout
 *
 * Layout wrapper for the Bimanova admin portal.
 * Contains a built-in platform role guard — non-admin users are redirected to /403.
 */

import { Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@/core/auth/auth.store';
import { useUIStore } from '@/state/stores/ui.store';
import { PLATFORM_ROLES } from '@/core/auth/constants';
import { cn } from '@/shared/lib';
import { AdminSidebar } from './components/admin-sidebar';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout = ({ children }: AdminLayoutProps) => {
  const user = useAuthStore((state) => state.user);
  const { isMobile, isTablet, setScreenWidth, isSidebarCollapsed } = useUIStore();

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setScreenWidth]);

  // Guard: only platform roles can access admin
  if (!user || !PLATFORM_ROLES.includes(user.role as (typeof PLATFORM_ROLES)[number])) {
    return <Navigate to="/403" replace />;
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-950">
        <div className="h-14 flex items-center px-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
              <span className="text-white font-bold text-xs">B</span>
            </div>
            <span className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">Bimanova</span>
            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-700 leading-none uppercase tracking-wide">
              Admin
            </span>
          </div>
        </div>
        <main className="pt-4 pb-8 px-4">{children}</main>
      </div>
    );
  }

  // Tablet Layout
  if (isTablet) {
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-950 flex">
        <AdminSidebar />
        <div className="flex-1 flex flex-col transition-all duration-300 ml-[72px]">
          <main className="flex-1 p-4 lg:p-6">{children}</main>
        </div>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 flex">
      <AdminSidebar />
      <div
        className={cn(
          'flex-1 flex flex-col transition-all duration-300',
          isSidebarCollapsed ? 'ml-[72px]' : 'ml-64'
        )}
      >
        <main className="flex-1 p-0">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
